import { get, writable } from 'svelte/store';
import { isModalDialogOpen } from '../sheetPopover';

export interface PopoverStoreConfig {
  /** Delay in ms before showing (default 0 = immediate) */
  showDelay?: number;
  /** Delay in ms before hiding (default 100) */
  hideDelay?: number;
  /**
   * True while the popover renders as a bottom sheet (mobile). Then delayed
   * (hover) hides don't close it, and a delayed show is dropped when a dialog
   * opened in the meantime. See lib/sheetPopover.ts.
   */
  sheetMode?: () => boolean;
}

export interface PopoverStore<T> {
  /** Svelte readable store holding current state (or null when hidden) */
  state: ReturnType<typeof writable<T | null>>;
  /** Show the popover with the given state. Cancels any pending hide. */
  show: (value: T) => void;
  /** Hide the popover. If immediate=true, clears instantly; otherwise after hideDelay. */
  hide: (immediate?: boolean) => void;
  /** Cancel a pending hide timeout (e.g. when cursor enters the popover). */
  cancelHide: () => void;
  /** Convenience: cancel both pending hide and show (cursor entered popover element). */
  enterPopover: () => void;
  /** Convenience: start a delayed hide (cursor left popover element). */
  leavePopover: () => void;
}

/**
 * Factory that creates a delayed show/hide popover store.
 *
 * All three simple tooltip/popover stores (projectTooltip, eventTooltip,
 * projectCellPopover) share this exact pattern — only the delay values
 * and state shape differ.
 */
export function createPopoverStore<T>(config: PopoverStoreConfig = {}): PopoverStore<T> {
  const { showDelay = 0, hideDelay = 100, sheetMode } = config;

  const state = writable<T | null>(null);

  let showTimeout: ReturnType<typeof setTimeout> | null = null;
  let hideTimeout: ReturnType<typeof setTimeout> | null = null;

  function clearShowTimeout() {
    if (showTimeout) {
      clearTimeout(showTimeout);
      showTimeout = null;
    }
  }

  function clearHideTimeout() {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  }

  function show(value: T): void {
    // Cancel any pending hide
    clearHideTimeout();

    // Cancel any pending show for a different value
    clearShowTimeout();

    if (showDelay > 0) {
      showTimeout = setTimeout(() => {
        showTimeout = null;
        if (sheetMode?.() && isModalDialogOpen()) return;
        state.set(value);
      }, showDelay);
    } else {
      state.set(value);
    }
  }

  function hide(immediate = false): void {
    // Always cancel pending show
    clearShowTimeout();

    if (immediate) {
      clearHideTimeout();
      state.set(null);
      return;
    }

    // A sheet is only closed on purpose (backdrop, swipe, Esc, an action).
    if (sheetMode?.() && get(state) !== null) return;

    clearHideTimeout();
    hideTimeout = setTimeout(() => {
      state.set(null);
      hideTimeout = null;
    }, hideDelay);
  }

  function cancelHide(): void {
    clearHideTimeout();
  }

  function enterPopover(): void {
    clearHideTimeout();
    clearShowTimeout();
  }

  function leavePopover(): void {
    hide();
  }

  return { state, show, hide, cancelHide, enterPopover, leavePopover };
}
